import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuditById, updateAuditStatus, addAuditComment, assignTeamToAudit, updateAuditArea } from '../../services/auditAPI';  // Add updateAuditArea service
import { fetchUsers } from '../../services/userAPI'; // Import fetchUsers to get available users
import { Button, Input, Select, Spin, message, Row, Col, Table } from 'antd';
import { useSelector } from 'react-redux'; 
import { getAreas } from '../../services/areaAPI';
const { Option } = Select;

const AuditDetailsPage = () => {
  const { id } = useParams(); // Get the audit ID from the URL
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [teamAssignments, setTeamAssignments] = useState([]);  // Store team assignments (with roles)
  const [availableUsers, setAvailableUsers] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);  // Store the areas
  const [selectedArea, setSelectedArea] = useState(null);  // Available users for assignment
  const [templates, setTemplates] = useState([]); // Available templates
  const userId = useSelector((state) => state.profile.user._id); // Get userId from Redux state
  // console.log("User ID from Redux:", userId); // Log the userId from Redux state

  useEffect(() => {
    fetchAuditDetails();
    fetchAvailableUsers();  // Fetch available users for team assignment
    fetchAreas();
  }, [id]);

  // Fetch areas
  const fetchAreas = async () => {
    try {
      const areas = await getAreas(userId); 
      setAreaOptions(areas); 
    } catch (err) {
      message.error('Error fetching areas');
    }
  };

  const fetchAuditDetails = async () => {
    setLoading(true);
    try {
      const data = await getAuditById(id); // Fetch the audit data
      setAudit(data);
      setTeamAssignments(data.assignedTo || []); // If team is already assigned, load the team assignments
      setSelectedArea(data.area || null);  // If area is already set in audit data, load it
    } catch (err) {
      message.error('Error fetching audit details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const users = await fetchUsers(userId);
      setAvailableUsers(users);
    } catch (err) {
      message.error('Error fetching available users');
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await updateAuditStatus(id, status);
      message.success('Audit status updated');
      fetchAuditDetails(); // Refresh audit details
    } catch (err) {
      message.error('Error updating status');
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) {
      message.error('Please enter a comment');
      return;
    }

    try {
      if (!userId) {
        message.error('User not logged in');
        return;
      }
      await addAuditComment(id, comment, userId);

      message.success('Comment added');
      setComment('');
      fetchAuditDetails();
    } catch (err) {
      message.error('Error adding comment');
    }
  };

  const handleTeamAssignment = async () => {
    try {
      // Prepare team data with userId and role
      const teamToAssign = teamAssignments.map((member) => ({
        userId: member.userId, // Only send userId
        role: member.role,      // Send role separately
      }));

      // Make the API request with the team data
      await assignTeamToAudit(id, teamToAssign);
      message.success('Team assigned successfully');
      fetchAuditDetails(); // Refresh to show assigned team
    } catch (err) {
      message.error('Error assigning team');
      console.error('Error:', err); // Log the full error
    }
  };

  const handleRoleChange = (userId, role) => {
    setTeamAssignments((prevAssignments) => {
      const updatedAssignments = prevAssignments.map((member) => {
        if (member.userId === userId) {
          return { ...member, role };
        }
        return member;
      });
      return updatedAssignments;
    });
  };

  const handleUserSelection = (selectedUserIds) => {
    // Map the selected user IDs to the team assignments, maintaining roles
    setTeamAssignments((prevAssignments) => {
      // Filter out users who were deselected
      const updatedAssignments = prevAssignments.filter((member) =>
        selectedUserIds.includes(member.userId)
      );

      // Add users who were selected but are not in the teamAssignments yet
      selectedUserIds.forEach((userId) => {
        if (!updatedAssignments.some((member) => member.userId === userId)) {
          updatedAssignments.push({ userId, role: 'Auditor' }); // Default role as 'Auditor'
        }
      });

      return updatedAssignments;
    });
  };

  // Handle area selection and save it in the audit data
  const handleAreaChange = async (areaId) => {
    setSelectedArea(areaId);
    try {
      // Send the selected area to the backend to update the audit data
      await updateAuditArea(id, areaId);
      message.success('Area updated successfully');
    } catch (err) {
      message.error('Error updating area');
    }
  };

  // Columns for the team assignments table
  const columns = [
    {
      title: 'First Name',
      dataIndex: 'userId',
      render: (userId) => {
        const user = availableUsers.find(user => user._id === userId);
        return user ? user.firstName : 'N/A'; // Fallback to 'N/A' if user is not found
      },
    },
    {
      title: 'Last Name',
      dataIndex: 'userId',
      render: (userId) => {
        const user = availableUsers.find(user => user._id === userId);
        return user ? user.lastName : 'N/A'; // Fallback to 'N/A' if user is not found
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      render: (role, record) => (
        <Select
          value={role}
          onChange={(value) => handleRoleChange(record.userId, value)}
          style={{ width: '150px' }}
        >
          <Option value="Auditor">Auditor</Option>
          <Option value="Chairman">Chairman</Option>
          <Option value="Team Member">Team Member</Option>
        </Select>
      ),
    },
  ];

  if (loading) {
    return <Spin size="large" />;
  }

  if (!audit) {
    return <p>No audit found</p>;
  }

  return (
    <div className=" min-h-screen">
      <div className='flex flex-row justify-between'>
      <Button type="default" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button onClick={() => navigate(`/audit/${id}`)}>Check Progress</Button>
      </div>
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-semibold mb-4">Audit Details</h2>

        <div className="mb-6">
          <h3 className="text-xl font-semibold">Title</h3>
          <p>{audit.title}</p>
        </div>

        <div className="mb-6">
          <h3 className="text-xl font-semibold">Description</h3>
          <p>{audit.description}</p>
        </div>

        {/* <div className="mb-6">
          <h3 className="text-xl font-semibold">Status</h3>
          <Select value={audit.status} onChange={handleStatusChange} style={{ width: '200px' }}>
            <Option value="Pending">Pending</Option>
            <Option value="In Progress">In Progress</Option>
            <Option value="Completed">Completed</Option>
          </Select>
        </div> */}

        {/* Area Selection */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold">Select Area</h3>
          <Select
            value={selectedArea}
            onChange={handleAreaChange}
            style={{ width: '100%' }}
            placeholder="Select an area"
          >
            {areaOptions.map((area) => (
              <Option key={area._id} value={area._id}>{area.name}</Option>
            ))}
          </Select>
        </div>

        {/* Assigned Team Section */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold">Assigned Team</h3>
          {audit.assignedTo && audit.assignedTo.length > 0 ? (
            <Table
              dataSource={audit.assignedTo.map((member, index) => ({
                key: index,
                userId: member.userId,
                role: member.role,
              }))}
              columns={columns}
              pagination={false}
            />
          ) : (
            <p>No team assigned yet</p>
          )}
        </div>

        {/* Team Assignment */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold">Assign Team</h3>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select team members"
            onChange={handleUserSelection}
          >
            {availableUsers.map((user) => (
              <Option key={user._id} value={user._id}>
                {user.firstName} {user.lastName} ({user.email})
              </Option>
            ))}
          </Select>

          {teamAssignments.length > 0 && (
            <div className="mt-4">
              <h4 className="text-lg">Assign Roles</h4>
              {teamAssignments.map((assignment, index) => {
                const user = availableUsers.find(u => u._id === assignment.userId); // Find user
                return (
                  <Row key={index} className="mb-4">
                    <Col span={12}>
                      <strong>{user ? `${user.firstName} ${user.lastName}` : 'User not found'}</strong>
                    </Col>
                    <Col span={12}>
                      <Select
                        value={assignment.role}
                        onChange={(value) => handleRoleChange(assignment.userId, value)}
                        style={{ width: '200px' }}
                      >
                        <Option value="Auditor">Auditor</Option>
                        <Option value="Chairman">Chairman</Option>
                        <Option value="Team Member">Team Member</Option>
                      </Select>
                    </Col>
                  </Row>
                );
              })}
            </div>
          )}

          <Button type="primary" onClick={handleTeamAssignment} className="mt-4">
            Assign Team
          </Button>
        </div>

        {/* Comments Section */}
        {/* <div className="mb-6">
          <h3 className="text-xl font-semibold">Comments</h3>
          <div>
            {audit.comments.map((comment, index) => (
              <div key={index} className="p-4 mb-2 bg-gray-50 border rounded-md">
                <p>{comment.text}</p>
              </div>
            ))}
          </div>

          <Input.TextArea
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment"
            className="mb-4"
          />
          <Button type="primary" onClick={handleAddComment}>
            Add Comment
          </Button>
        </div> */}

        
      </div>
    </div>
  );
};

export default AuditDetailsPage;
